import {Fragment, ReactElement, ReactNode} from 'react'
import {
  Alert,
  Box,
  Button,
  CardContent,
  CircularProgress,
  Typography
} from '@mui/material'
import kc from 'web-common/services/auth'
import TimelineEventTemplate from 'web-common/views/timeline/TimelineEventTemplate'
import {WithTranslation, withTranslation} from 'react-i18next'
import AssistantEventBus from './AssistantEventBus'
import {AssistantEventProducer} from './AssistantEventProducer'
import MapLocationPicker from './actions/MapLocationPicker'
import DateTimePicker from './actions/DateTimePicker'
import ConfirmCreateDemand from './actions/ConfirmCreateDemand'
import FileStorage from 'web-common/services/files/FileStorage'
import withRouter, {RouteComponentProps} from 'web-common/utilities/router'
import {WS_ASSISTANT_METADATA} from '@fnd/timeline'
import DemandViewButton from './buttons/DemandViewButton'
import {BlueCard} from 'web-common/views/timeline/styled/BlueCard'
import {GreyCard} from 'web-common/views/timeline/styled/GreyCard'

export interface AssistantMessageEventProps {
  userId: string
  message: string | ReactElement
  date: number
  sent?: boolean
  eventId: string
  producer: AssistantEventProducer
  metadata?: WS_ASSISTANT_METADATA
}

interface InternalProps
  extends AssistantMessageEventProps,
    RouteComponentProps,
    WithTranslation {}

interface AssistantMessageEventState {
  inRequest: boolean
  attachment?: string
  debugMode?: boolean
  message: string
}

class AssistantMessageEvent extends TimelineEventTemplate<
  InternalProps,
  AssistantMessageEventState
> {
  state: AssistantMessageEventState = {
    inRequest: false,
    attachment: undefined,
    message: typeof this.props.message === 'string' ? this.props.message : ''
  }

  async componentDidMount() {
    const params = new URLSearchParams(this.props.location.search)
    const debugMode = params.get('debug') === 'true'

    if (debugMode) {
      this.setState({debugMode})
    }

    if (this.props.metadata?.attachment) {
      const attachmentUrl = await FileStorage.getResource(
        this.props.metadata.attachment,
        true
      )
      this.setState({attachment: attachmentUrl})
    }
  }

  async resendMessage() {
    if (!AssistantEventBus.connection.value) {
      await AssistantEventBus.connect().catch()
    }

    this.setState({inRequest: true}, async () => {
      await this.props.producer.reCreateEvent(this.props.eventId)
      this.setState({inRequest: false})
    })
  }

  addActionByQuestionType(questionType: string) {
    switch (questionType) {
      case 'location-picker':
        return (
          <MapLocationPicker
            variant="location"
            buttonLabel={
              this.props.metadata?.buttonLabel ??
              this.props.t('fnd-assistant-action-location-title')
            }
            position="flex-start"
            {...this.props}
          />
        )
      case 'location-picker-range':
        return (
          <MapLocationPicker
            variant="range"
            buttonLabel={
              this.props.metadata?.buttonLabel ??
              this.props.t('fnd-assistant-action-location-title')
            }
            position="flex-start"
            {...this.props}
          />
        )
      case 'date-picker':
        return (
          <DateTimePicker
            buttonLabel={
              this.props.metadata?.buttonLabel ??
              this.props.t('fnd-assistant-dialog-date-title')
            }
            position="flex-start"
            {...this.props}
          />
        )
    }
  }

  isRTL(text: string | ReactElement) {
    if (typeof text !== 'string') return false
    const regex = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/
    return regex.test(text)
  }

  parseRichText(message: string | ReactNode) {
    if (typeof message !== 'string') return message
    return message.split(/(\*\*[^*]+\*\*)/g).map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>
      }
      return <Fragment key={index}>{part}</Fragment>
    })
  }

  render() {
    const UserCard =
      this.props.userId === kc.instance.tokenParsed?.sub ? BlueCard : GreyCard
    const direction = this.isRTL(this.state.message) ? 'rtl' : 'ltr'

    if (this.props.sent === false) {
      this.props.producer.actionManager.removeThinkingIndicator()
    }

    if (this.props.metadata?.demandId) {
      this.props.producer.setDemandId(this.props.metadata.demandId)
    }

    return (
      <Box
        display={'flex'}
        flexDirection={'column'}
        alignItems={'inherit'}
        gap={'0.25rem'}
        position={'relative'}
        width={'80%'}
      >
        <UserCard variant={'outlined'}>
          <CardContent>
            {/* Message text */}
            {this.state.message && (
              <Typography
                variant={'body1'}
                style={{whiteSpace: 'pre-line'}}
                dir={direction}
              >
                {this.parseRichText(this.state.message)}
              </Typography>
            )}

            {/* Attached image */}
            {this.state.attachment && (
              <img src={this.state.attachment} width={400} alt="Attachment" />
            )}

            {/* Show location or date picker */}
            {this.props.metadata?.questionType &&
              this.addActionByQuestionType(this.props.metadata.questionType)}

            {/* Confirm demand create */}
            {this.props.metadata?.sessionCompleted && (
              <ConfirmCreateDemand
                buttonLabel={
                  this.props.metadata.buttonLabel ??
                  this.props.t('fnd-assistant-action-confirm')
                }
                position="flex-start"
                {...this.props}
              />
            )}

            {/* Link to demand in job requests */}
            {this.props.metadata?.demandId && UserCard === GreyCard && (
              <DemandViewButton
                demandId={this.props.metadata.demandId}
                buttonLabel={this.props.metadata?.buttonLabel}
              />
            )}

            {/* Show metadata in debug mode */}
            {this.state.debugMode && this.props.metadata && (
              <Box
                component="pre"
                sx={{
                  marginTop: 2,
                  padding: 1,
                  whiteSpace: 'pre-wrap',
                  typography: 'body1',
                  color: '#FFFDB5',
                  backgroundColor: '#444444',
                  borderRadius: 1
                }}
              >
                metadata: {JSON.stringify(this.props.metadata, null, 2)}
              </Box>
            )}
          </CardContent>
        </UserCard>

        {/* Error: message sent failed */}
        {this.props.sent === false && (
          <Alert severity={'error'} sx={{alignItems: 'center'}}>
            {this.props.t('common:fnd-common-event-message-send-failed')}
            <Button
              color={'error'}
              size={'small'}
              variant={'text'}
              onClick={this.resendMessage.bind(this)}
              disabled={this.state.inRequest}
            >
              {this.state.inRequest ? (
                <CircularProgress />
              ) : (
                this.props.t('common:fnd-common-event-resend-message')
              )}
            </Button>
          </Alert>
        )}
      </Box>
    )
  }
}

export default withRouter(withTranslation()(AssistantMessageEvent))
